import { HttpErrorResponse } from '@angular/common/http';
import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ICON } from '@core/util/icon';
import { MatterBusinessUser, MatterFileType } from '@models/matter';
import { CreateDealRequest } from '@models/title-insurance/title-plus';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { NavigationService } from '@services/nav/nav.service';
import { TitlePlusService } from '@services/title-plus/title-plus.service';
import { TitlePlusSelectors } from '@state/global/title-insurance/title-plus';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { firstValueFrom } from 'rxjs';
import { Logger } from 'src/app/shared/core/logger';
import { environment } from 'src/environments/environment';

export interface TitlePlusModalData {
	submitNewOrder: boolean;
}

@Component({
	selector: 'app-title-plus-modal',
	templateUrl: './title-plus-modal.component.html',
	styleUrls: ['./title-plus-modal.component.scss'],
	standalone: false,
})
export class TitlePlusModalComponent implements OnInit {
	@SelectSnapshot(SelectedMatter.id) matterId: string;
	@SelectSnapshot(SelectedMatter.matterName) matterName: string;
	@SelectSnapshot(SelectedMatter.matterNumber) matterNumber: string;
	@SelectSnapshot(TitlePlusSelectors.getTitlePlusDataPayload)
	payload: CreateDealRequest;
	@SelectSnapshot(SelectedMatter.matterFileType) fileType: MatterFileType;
	@SelectSnapshot(SelectedMatter.titleInsuranceThirdPartyId)
	thirdPartyId: string;
	@SelectSnapshot(SelectedMatter.assignedLawyer)
	assignedLawyer: MatterBusinessUser;

	public ICON = ICON;
	public loading = false;
	public iframeUrl: SafeResourceUrl;
	public backButtonLoading = false;
	public submitNewOrder = false;
	public errorMessages: string[] = [];
	public errorMessage: string;
	public AUTH_ERROR = 'Unable to connect to TitlePLUS';
	public hasError = false;
	public integrationsPage = '';

	constructor(
		private sanitizer: DomSanitizer,
		private dialogRef: MatDialogRef<TitlePlusModalComponent>,
		private titlePlusService: TitlePlusService,
		private navigation: NavigationService,
		@Inject(MAT_DIALOG_DATA) public _data: TitlePlusModalData,
	) {
		this.submitNewOrder = _data?.submitNewOrder;
		this.dialogRef.disableClose = true;
		this.dialogRef.addPanelClass(['vw-90', 'vh-90']);
	}

	ngOnInit() {
		this.createDeal();
	}

	goToIntegration() {
		this.dialogRef.close();
		this.navigation.goToProfile_Integrations_TitlePLUS();
	}

	createDeal() {
		this.loading = true;

		const payload = {
			...this.payload,
			PolicyOrder: {
				...this.payload.PolicyOrder,
				SecureId: this.submitNewOrder ? 'new' : this.thirdPartyId,
			},
		};

		Logger.info('Title Plus - create deal payload', payload);

		firstValueFrom(this.titlePlusService.createDealRequest(payload))
			.then((res) => {
				Logger.info('title plus response', res);
				this.hasError = false;
				this.iframeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
					`${environment.titlePlus.baseUrl}/policy?id=${res.orderId}&access_token=${res.accessToken}`,
				);
			})
			.catch((error: HttpErrorResponse) => {
				Logger.exception(error, { message: 'Title Plus - create deal error' });
				this.hasError = true;

				if (Array.isArray(error.error)) {
					this.errorMessages = error.error;
				}

				if (
					typeof error.error?.errors === 'object' &&
					error.error.errors !== null
				) {
					this.errorMessages = Object.entries(error.error?.errors).map(
						([key, value]) => `${key}: ${value}`,
					);
				}

				this.errorMessage =
					error.error?.detail || error.error?.message || 'An error occurred';
			})
			.finally(() => {
				this.loading = false;
			});
	}

	beforeClose() {
		this.iframeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
			'http://www.google.com',
		);
		this.backButtonLoading = true;
		this.titlePlusService.updateStatus();
		this.dialogRef.close();
	}
}
